import { useQuery } from '@tanstack/react-query';
import api from '../../http';
import { OCR_TIMEOUT_MS } from './ocr.constants';

export interface OcrMeasure {
	value: number;
	unit: string;
}

export interface OcrSoilSample {
	sampleIndex: number;
	sampleLabId: string;
	sampleLabel: string;
	depthCm: string;
	Pm1: OcrMeasure;
	Pres: OcrMeasure;
	K: OcrMeasure;
	Ca: OcrMeasure;
	Mg: OcrMeasure;
	S: OcrMeasure;
	B: OcrMeasure;
	Cu: OcrMeasure;
	Fe: OcrMeasure;
	Mn: OcrMeasure;
	Zn: OcrMeasure;
	pHagua: OcrMeasure;
	pHCaCl2: OcrMeasure;
	Al: OcrMeasure;
	HAl: OcrMeasure;
	MO: OcrMeasure;
	Na: OcrMeasure;
	Prem: OcrMeasure;
	SB: OcrMeasure;
	ctcE: OcrMeasure;
	ctcT: OcrMeasure;
	CaMg: OcrMeasure;
	satSB: OcrMeasure;
	satAl: OcrMeasure;
	satCa: OcrMeasure;
	satMg: OcrMeasure;
	satK: OcrMeasure;
}

export interface OcrFoliarSample {
	sampleIndex: number;
	sampleLabId: string;
	sampleLabel: string;
	N: OcrMeasure;
	P: OcrMeasure;
	K: OcrMeasure;
	Ca: OcrMeasure;
	Mg: OcrMeasure;
	S: OcrMeasure;
	B: OcrMeasure;
	Cu: OcrMeasure;
	Fe: OcrMeasure;
	Mn: OcrMeasure;
	Zn: OcrMeasure;
}

export interface OcrVerifyResult<TSample extends OcrSoilSample | OcrFoliarSample> {
	jobId: string;
	samples: TSample[];
}

export interface OcrVerifyJobResponse<TSample extends OcrSoilSample | OcrFoliarSample> {
	jobId: string;
	status: 'queued' | 'processing' | 'done' | 'failed';
	analysisType: 'soil' | 'foliar';
	createdAt: string;
	updatedAt: string;
	queueName?: string;
	fileType?: string;
	sentAt?: string;
	error?: { message?: string };
	result?: OcrVerifyResult<TSample>;
}

export const useGetOcrVerify = <TSample extends OcrSoilSample | OcrFoliarSample = OcrSoilSample>(
	jobId: string,
	enabled = true
) => {
	return useQuery<OcrVerifyJobResponse<TSample>>({
		queryKey: ['ocrVerify', jobId],
		queryFn: async ({ signal }) => {
			const { data } = await api.get<OcrVerifyJobResponse<TSample>>(`/ocr/verifyJob/${jobId}`, { timeout: OCR_TIMEOUT_MS, signal });
			return data;
		},
		enabled: !!jobId && enabled,
		refetchInterval: (query) => {
			const data = query.state.data;

			if (!data) {
				return 3000;
			}

			if (data.status === 'done' || data.status === 'failed') {
				return false;
			}

			return 3000;
		},
		refetchIntervalInBackground: true,
	});
};
